import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { auditTime, BehaviorSubject, distinctUntilChanged, filter, firstValueFrom, map, merge, Observable, skip, Subject, takeUntil, timeout } from 'rxjs';
import { TokenStoreService } from './token-store.service';
import {
  AuthToken,
  ApiException,
  BanShortResponse,
  Body,
  Body2,
  Client,
  FileResponse,
  LangResponse,
  LoginSmsRequest,
  Message,
  ReplyOnResolveDispute,
  SendSmsRequest,
  SkillsRequest,
  SubscriptionsInfo,
  UserDataResponse,
  UserResponse,
  VisitDataRequest,
} from '@api/client';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';
import { DatePipe } from '@angular/common';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { LocalStorageService } from '@services/local-storage.service';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { LoadingService } from './loading.service';
import { OnlineStateService } from './online-state.service';
import { AppStateChangeService } from './app-state-change.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';
import { JwtService } from './jwt.service';
import { AppInfoService } from './app-info.service';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { WindowService } from './window.service';
import { HttpStatusCode } from '@angular/common/http';
import { environment } from '@environments/environment';

interface AppleProfile {
  user: string;
  email: string | null;
  givenName: string | null;
  familyName: string | null;
}

interface GoogleProfile {
  email: string | null;
  familyName: string | null;
  givenName: string | null;
  id: string | null;
  name: string | null;
  imageUrl: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly client = inject(Client);
  private readonly tokenStore = inject(TokenStoreService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly onlineStateService = inject(OnlineStateService);
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly jwtService = inject(JwtService);
  private readonly appInfoService = inject(AppInfoService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly window = inject(WindowService);
  private readonly localStorage = inject(LocalStorageService);

  private static readonly REQUEST_TIMEOUT_MS = 15000;

  public readonly userDataLoading = signal(false);
  public readonly userDataLoadFailed = signal(false);

  public appleUser: AppleProfile;
  public googleUser: GoogleProfile;
  public readonly isAuthenticated$: Observable<boolean>;

  private refreshTokenMutex: Promise<void> = null;
  private parsedTokenSubj: BehaviorSubject<ParsedToken | null> = new BehaviorSubject(null);
  private parsedUserSubj: BehaviorSubject<ParsedUser | null> = new BehaviorSubject(null);
  private sb: SubscriptionsBag = new SubscriptionsBag();
  private userDataLoadPromise: Promise<void> | null = null;
  private initPromise: Promise<void> | null = null;
  private tokenWritePromise: Promise<void> = Promise.resolve();
  private readonly sessionChanged = new Subject<void>();
  private sessionRevision = 0;
  private initialized = false;
  private visitDataPending = false;
  private userDataReloadPending = false;

  public get parsedToken$(): Observable<ParsedToken | null> {
    return this.parsedTokenSubj.asObservable();
  }

  public get parsedToken(): ParsedToken | null {
    return this.parsedTokenSubj.value;
  }

  private set parsedToken(parsedToken: ParsedToken | null) {
    if (this.parsedToken?.userId !== parsedToken?.userId) {
      this.sessionRevision++;
      this.refreshTokenMutex = null;
      this.userDataReloadPending = false;
      this.userDataLoadPromise = null;
      this.sessionChanged.next();
      this.parsedUser = null;
      this.userDataLoading.set(false);
      this.userDataLoadFailed.set(false);
    }
    this.parsedTokenSubj.next(parsedToken);
  }

  public get parsedUser$(): Observable<ParsedUser | null> {
    return this.parsedUserSubj.asObservable();
  }

  public get parsedUser(): ParsedUser | null {
    return this.parsedUserSubj.value;
  }

  private set parsedUser(parsedUser: ParsedUser | null) {
    this.parsedUserSubj.next(parsedUser);
  }

  public get isAuthenticated(): boolean {
    return !!this.parsedToken;
  }

  public get sessionVersion(): number {
    return this.sessionRevision;
  }

  public get isUserProfileIncomplete(): boolean {
    const parsedUser: ParsedUser = this.parsedUser;
    return parsedUser && (!parsedUser?.name || !parsedUser?.surname);
  }

  constructor() {
    this.isAuthenticated$ = this.parsedTokenSubj.pipe(
      map((parsedToken: ParsedToken | null): boolean => !!parsedToken),
      distinctUntilChanged(),
    );
    this.sb.sub = merge(
      this.onlineStateService.isOffline$.pipe(distinctUntilChanged(), skip(1), filter(isOffline => !isOffline)),
      this.appStateChangeService.appStateChange$.pipe(distinctUntilChanged(), skip(1), filter(isActive => isActive)),
    ).pipe(auditTime(0)).subscribe((): void => {
      if (this.initialized) this.synchronizeSessionData();
    });
  }

  ngOnDestroy(): void {
    this.initialized = false;
    this.sessionRevision++;
    this.sessionChanged.next();
    this.sessionChanged.complete();
    this.sb.unsubscribeAll();
  }

  public init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    const promise = this.restoreSession().finally((): void => {
      if (this.initPromise === promise) this.initPromise = null;
    });
    this.initPromise = promise;
    return promise;
  }

  public ensureInitialized(): Promise<void> {
    return this.initialized ? Promise.resolve() : this.init();
  }

  public reloadUserData(): Promise<void> {
    const userId = this.parsedToken?.userId;
    if (!userId || this.onlineStateService.isOffline) return Promise.resolve();
    return this.loadUserData(userId);
  }

  public setParsedUser(userDataResponse: UserDataResponse): void {
    if (userDataResponse?.data?.id !== this.parsedToken?.userId) return;
    this.userDataLoadPromise = null;
    this.userDataReloadPending = false;
    this.sessionChanged.next();
    this.userDataLoading.set(false);
    this.parsedUser = ParsedUser.fromResponse(userDataResponse?.data);
    this.userDataLoadFailed.set(false);
  }

  public async loginWithToken(request: Body): Promise<void> {
    const revision = this.sessionRevision;
    try {
      const authToken: AuthToken = await firstValueFrom(this.client.authLogin(request));

      if (authToken?.user?.role.includes('user') || authToken?.user?.role.includes('admin')) {
        await this.loginUserIfTokenAvailable(authToken, revision);
      }
    } catch (error) {
      await this.authExecutionHandler(error);
    }
  }

  public async loginWithSmsCode(request: LoginSmsRequest): Promise<void> {
    const revision = this.sessionRevision;
    try {
      await this.loadingService.start();

      const authToken: AuthToken = await firstValueFrom(this.client.authLoginSms(request));
      if (authToken?.user?.role.includes('user') || authToken?.user?.role.includes('admin')) {
        await this.loginUserIfTokenAvailable(authToken, revision);
      } else {
        console.error('Invalid user role:', authToken?.user?.role);
      }
    } catch (error) {
      await this.authExecutionHandler(error);
      throw error;
    } finally {
      await this.loadingService.stop();
    }
  }

  public async sendSmsCodeRequest(request: SendSmsRequest): Promise<string> {
    try {
      const response: Message = await firstValueFrom(this.client.authSendSms(request));
      return response?.message;
    } catch (error) {
      if (error?.status === HttpStatusCode.TooManyRequests) {
        throw error;
      }

      console.error('Send SMS code request error:', error);
      await this.authExecutionHandler(error);
    }
  }

  public async authMe(): Promise<UserDataResponse> {
    try {
      return await firstValueFrom(this.client.authMe());
    } catch (error) {
      console.error(error);
    }
  }

  public refreshToken(): Promise<void> {
    if (this.refreshTokenMutex) return this.refreshTokenMutex;
    const promise = this.refreshSessionToken(this.sessionRevision).finally((): void => {
      if (this.refreshTokenMutex === promise) this.refreshTokenMutex = null;
    });
    this.refreshTokenMutex = promise;
    return promise;
  }

  public async banFilter(type: number, callback: Function = null): Promise<void> {
    if (!this.isAuthenticated) {
      if (typeof callback === 'function') await callback();
      return;
    }

    const userData: UserDataResponse = await this.authMe();
    const banShort: BanShortResponse = userData?.data?.bans.find((ban: BanShortResponse) => (ban?.type == type) && (new Date(ban?.date)).getTime() > (new Date()).getTime());

    if (!banShort) {
      if (typeof callback === 'function') await callback();
    } else {
      const untilDate: string = this.datePipe.transform(banShort?.date, 'dd.MM.yyyy hh:mm');
      await this.toastService.error(this.translate.instant(
        (type == 2) ? 'ban.task' : 'ban.reply',
        {date: untilDate, reason: banShort?.reason})
      );
    }
  }

  public async logout(): Promise<void> {
    console.warn('Logout...');
    await this.clearTokens();

    try {
      const withApple: boolean = this.localStorage.getItem('withApple');
      const withGoogle: boolean = this.localStorage.getItem('withGoogle');

      if (withApple || withGoogle) {
        await SocialLogin.logout({provider: (withApple) ? 'apple' : 'google'});
      }
    } catch (error) {
      console.error(error);
    }

    if (typeof window !== 'undefined') {
      location.replace('/login'); // reload page
    }
  }

  private async sendVisitData(): Promise<void> {
    const body: VisitDataRequest = new VisitDataRequest({
      device: await this.phoneInfoService.getDeviceModelWithOS(),
      app_version: await this.appInfoService.getShortAppVersion(),
    });

    if (!this.parsedToken) {
      body.temp_token = this.tokenStore.getTempToken();
      body.resolution = this.window.getScreenResolution();
    }
    await firstValueFrom(this.client.userVisitData(body));
  }

  private async authExecutionHandler(error: any): Promise<void> {
    const responseMessage: string = error?.result?.error ?? error?.result?.message;

    if (responseMessage === 'auth.user_not_found') {
      await this.toastService.error(this.translate.instant(responseMessage));
    } else if (responseMessage === 'auth.sms_service_not_available_for_your_mobile_operator') {
      await this.toastService.error(this.translate.instant(responseMessage));
    } else if (responseMessage === 'auth.invalid_code') {
      // processing in function smsCodeChanged
    } else {
      // await this.httpErrorHandlerService.handleHttpException(ex);
      const parsedException = JSON.parse(error?.response);
      const message: string = responseMessage ?? parsedException?.error ?? parsedException?.message;
      console.error('Login error:', error);
      await this.toastService.error(this.translate.instant(message));
    }
    throw error;
  }

  private async loginUserIfTokenAvailable(authToken: AuthToken, revision: number): Promise<void> {
    if (revision !== this.sessionRevision) return;
    if (!authToken) {
      console.error('No auth token, logout...');
      await this.toastService.warning(this.translate.instant('auth.unauthorized'));
      return await this.logout();
    }

    await this.saveTokens(authToken, revision);
  }

  private async saveTokens(authToken: AuthToken, revision: number): Promise<void> {
    this.tokenWritePromise = this.tokenWritePromise.then(async (): Promise<void> => {
      if (revision !== this.sessionRevision) return;
      await this.tokenStore.setTokens(authToken?.access_token, authToken?.refresh_token);
      if (revision !== this.sessionRevision) return;
      this.parsedToken = ParsedToken.fromToken(authToken?.access_token);
      this.parsedUser = ParsedUser.fromResponse(authToken?.user);
      this.initialized = true;
    }).catch((error: unknown): void => {
      console.error(error);
    });
    await this.tokenWritePromise;
  }

  private async clearTokens(): Promise<void> {
    if (!this.parsedToken) this.sessionRevision++;
    this.parsedToken = null;
    this.parsedUser = null;
    this.tokenWritePromise = this.tokenWritePromise.then(async (): Promise<void> => {
      await this.tokenStore.removeTokens();
    }).catch((error: unknown): void => {
      console.error(error);
    });
    await this.tokenWritePromise;
  }

  private async restoreSession(): Promise<void> {
    const revision = this.sessionRevision;
    try {
      await this.tokenWritePromise;
      const jwtToken = await this.tokenStore.getToken();
      let parsedToken = ParsedToken.fromToken(jwtToken);
      if (!parsedToken) {
        const refreshToken = await this.tokenStore.getRefreshToken();
        if (refreshToken && !this.jwtService.checkIsTokenExpired(refreshToken)) {
          parsedToken = ParsedToken.fromToken(refreshToken, 'id');
        }
      }
      if (revision !== this.sessionRevision) return;
      this.parsedToken = parsedToken;
      this.initialized = true;
      this.visitDataPending = true;
      this.synchronizeSessionData();
    } catch (error) {
      console.error('Session restore failed', error);
    }
  }

  private synchronizeSessionData(): void {
    if (this.onlineStateService.isOffline || !this.appStateChangeService.appIsActive) return;
    if (this.visitDataPending) {
      this.visitDataPending = false;
      void this.sendVisitData().catch((error: unknown): void => console.error('sendVisitData error', error));
    }
    if (this.userDataLoadPromise) {
      this.userDataReloadPending = true;
      return;
    }
    void this.reloadUserData();
  }

  private loadUserData(userId: number): Promise<void> {
    if (this.userDataLoadPromise) return this.userDataLoadPromise;
    const revision = this.sessionRevision;
    this.userDataLoading.set(true);
    this.userDataLoadFailed.set(false);
    const promise = firstValueFrom(this.client.userGet(String(userId)).pipe(
      timeout(AuthService.REQUEST_TIMEOUT_MS),
      takeUntil(merge(this.sessionChanged, this.onlineStateService.isOffline$.pipe(filter(isOffline => isOffline)))),
    )).then((response: UserDataResponse): void => {
      if (revision !== this.sessionRevision || this.userDataLoadPromise !== promise) return;
      if (response?.data?.id !== userId) throw new Error('Unexpected profile identity');
      this.parsedUser = ParsedUser.fromResponse(response.data);
    }).catch((error: unknown): void => {
      if (revision !== this.sessionRevision || this.userDataLoadPromise !== promise || this.onlineStateService.isOffline) return;
      this.userDataLoadFailed.set(true);
      console.error('loadUserData error', error);
    }).finally((): void => {
      if (this.userDataLoadPromise !== promise) return;
      this.userDataLoadPromise = null;
      this.userDataLoading.set(false);
      if (this.userDataReloadPending) {
        this.userDataReloadPending = false;
        this.synchronizeSessionData();
      }
    });
    this.userDataLoadPromise = promise;
    return promise;
  }

  private async refreshSessionToken(revision: number): Promise<void> {
    const refreshToken = await this.tokenStore.getRefreshToken();
    if (revision !== this.sessionRevision) throw new Error('Session changed');
    if (!refreshToken || this.jwtService.checkIsTokenExpired(refreshToken)) {
      const logout = this.logout();
      await this.toastService.warning(this.translate.instant('auth.unauthorized'));
      await logout;
      throw new Error('Refresh token is missing or expired');
    }
    try {
      const body = new Body2({refresh_token: refreshToken});
      const authToken = await firstValueFrom(this.client.authRefresh(body).pipe(timeout(AuthService.REQUEST_TIMEOUT_MS)));
      if (revision !== this.sessionRevision) throw new Error('Session changed');
      await this.loginUserIfTokenAvailable(authToken, revision);
      if (revision !== this.sessionRevision) throw new Error('Session changed');
    } catch (error) {
      if (revision !== this.sessionRevision) throw error;
      if (error instanceof ApiException && error.status === HttpStatusCode.Unauthorized) {
        const logout = this.logout();
        await this.toastService.warning(this.translate.instant('auth.unauthorized'));
        await logout;
      }
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  // private handleError(): Observable<never> {
  //   return throwError(this.translate.instant('notifications.badData'));
  // }

  // public getUserToken() {
  //   return JSON.parse(localStorage.getItem(this.JWT_TOKEN));
  // }

  // public getUserInfo() {
  //   return JSON.parse(localStorage.getItem(this.USER_INFO));
  // }

  //   public isAdmin() {
  //     const userInfo = this.getUserInfo();
  //     if (userInfo) {
  //       return userInfo?.role.includes('admin');
  //     }
  //     return false;
  //   }

  //   public isUser() {
  //     const userInfo = this.getUserInfo();
  //     if (userInfo) {
  //       return userInfo?.role.includes('user');
  //     }
  //     return false;
  //   }
}

export class ParsedToken {
  public userId: number = null;
  public expirationDate: Date = null;

  public static fromToken(jwtToken: string, subject: 'sub' | 'id' = 'sub'): ParsedToken {
    if (!jwtToken) return null;

    let jwtPayload: JwtPayload & {id?: number};
    try {
      jwtPayload = jwtDecode(jwtToken);
    } catch {
      return null;
    }
    const userId = Number(jwtPayload[subject]);
    if (!Number.isSafeInteger(userId) || userId <= 0 || !Number.isFinite(jwtPayload.exp)) return null;

    const newParsedToken: ParsedToken = new ParsedToken();
    newParsedToken.userId = userId;
    newParsedToken.expirationDate = new Date(jwtPayload.exp * 1000);

    if (!environment.production) console.info('\x1b[33m' + 'Token expiration date:' + '\x1b[0m', newParsedToken.expirationDate);

    return newParsedToken;
  }
}

export class ParsedUser {
  public id: number | null = null;
  public name: string | null = null;
  public surname: string | null = null;
  public nickname: string | null = null;
  public phone: string | null = null;
  public email: string | null = null;
  public avatar: FileResponse | null = null;
  public description: string | null = null;
  public rate: number | null = null;
  public rating: number | null = null;
  public skills: SkillsRequest[] | null = null;
  public lang: LangResponse | null = null;
  public isOpenForProposals: boolean | null = null;
  public replyOnResolveDispute: ReplyOnResolveDispute | null = null;
  public subscriptionsInfo?: SubscriptionsInfo;
  public official: boolean | null = null;

  public static fromResponse(response: UserResponse): ParsedUser {
    if (!response) return null;

    const parsedUser: ParsedUser = new ParsedUser();

    parsedUser.id = response?.id;
    parsedUser.name = response?.name;
    parsedUser.surname = response?.surname;
    parsedUser.nickname = response?.nickname;
    parsedUser.phone = response?.phone;
    parsedUser.email = response?.email;
    parsedUser.avatar = response?.avatar;
    parsedUser.description = response?.description;
    parsedUser.rate = response?.rate;
    parsedUser.rating = response?.rating;
    parsedUser.skills = response?.skills;
    parsedUser.lang = response?.lang;
    parsedUser.isOpenForProposals = response?.is_open_for_proposals;
    parsedUser.replyOnResolveDispute = response?.replyOnResolveDispute;
    parsedUser.subscriptionsInfo = response?.subscriptionsInfo;
    parsedUser.official = response?.official;

    return parsedUser;
  }
}

